"use client";

import { createContext, useContext, useState, useEffect } from 'react';
import type { AuthChangeEvent } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { authSessionLoad } from '@/lib/perf/authSessionLoad';

export type StudentProfile = {
  id: string;
  vid: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  preferredName: string | null;
  phone: string | null;
  fy: string | null;
  track: string | null;
  groupName: string | null;
  fallEnrolled: boolean;
  springEnrolled: boolean;
  summerEnrolled: boolean;
  role: string;
  overdueCount: number;
};

/** Returns the name to display for a student in the UI.
 *  Uses preferred_name if set, falls back to first_name. */
export function displayName(student: StudentProfile): string {
  return `${student.preferredName || student.firstName} ${student.lastName}`;
}

interface StudentsContextValue {
  students: StudentProfile[];
  loading: boolean;
  getStudentById: (id: string) => StudentProfile | undefined;
  getStudentByVid: (vid: string) => StudentProfile | undefined;
  refresh: () => Promise<void>;
}

function toStudent(row: Record<string, unknown>): StudentProfile {
  return {
    id:             row.id as string,
    vid:            (row.vid as string) ?? '',
    firstName:      (row.first_name as string) ?? '',
    lastName:       (row.last_name as string) ?? '',
    middleName:     (row.middle_name as string | null) ?? null,
    preferredName:  (row.preferred_name as string | null) ?? null,
    phone:          (row.phone as string | null) ?? null,
    fy:             (row.fy as string | null) ?? null,
    track:          (row.track as string | null) ?? null,
    groupName:      (row.group_name as string | null) ?? null,
    fallEnrolled:   (row.fall_enrolled as boolean) ?? false,
    springEnrolled: (row.spring_enrolled as boolean) ?? false,
    summerEnrolled: (row.summer_enrolled as boolean) ?? false,
    role:           (row.role as string),
    overdueCount:   (row.overdue_count as number) ?? 0,
  };
}

const StudentsContext = createContext<StudentsContextValue | null>(null);

export function StudentsProvider({ children }: { children: React.ReactNode }) {
  const supabase = createClient();
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [loading, setLoading] = useState(true);

  // Some devices reach this app over a Tailscale hostname (see allowedDevOrigins in
  // next.config.ts — phones/tablets on the tailnet), whose page loads/HMR ride the
  // tailnet tunnel but whose client-side Supabase calls go out over the device's own
  // WiFi/cellular link. A dropped cell signal or captive-portal blip can outlast a
  // single retry, so back off across a few attempts before giving up.
  const RETRY_DELAYS_MS = [2500, 5000, 10000];

  async function fetchStudents(retryAttempt = 0) {
    if (!navigator.onLine) { setLoading(false); return; }
    setLoading(true);
    try {
      // `student_directory`, not `profiles` — this context is mounted app-wide
      // (providers.tsx), so it runs for students too, and reading `profiles`
      // directly meant every student could pull every other student's vid,
      // phone, fy, track, group and overdue count (RLS gates rows, not columns).
      // The view masks those to NULL unless the viewer is staff, and toStudent()
      // above already null-coalesces every one of them, so staff behaviour is
      // byte-for-byte unchanged and students simply get blanks for fields the
      // collaborator picker never displays. Cast: student_directory isn't in
      // the generated database.types.ts (same reason table25() exists).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('student_directory')
        .select(
          'id, vid, first_name, last_name, middle_name, preferred_name, phone, fy, track, group_name, fall_enrolled, spring_enrolled, summer_enrolled, role, overdue_count'
        )
        .eq('role', 'student')
        .order('last_name')
        .order('first_name');
      if (error) {
        if (navigator.onLine && !error.message?.toLowerCase().includes('offline')) {
          console.error('[StudentsContext] fetch error:', error.message);
        }
      } else if (data) {
        setStudents((data as Record<string, unknown>[]).map(toStudent).filter((s: StudentProfile) =>
          s.groupName !== '__dev_auto_login__'
          && !(s.firstName === 'Dev' && s.lastName.includes('(Auto)'))
        ));
      }
    } catch (err) {
      // navigator.onLine reflects link-layer connectivity, not real reachability —
      // it stays true through brief DNS/TLS blips, so the 'online' event below never
      // fires to trigger a retry. Give those transient failures a few delayed
      // retries (with backoff) before treating it as a real error.
      const delay = RETRY_DELAYS_MS[retryAttempt];
      if (err instanceof TypeError && /fetch/i.test(err.message) && navigator.onLine && delay !== undefined) {
        setTimeout(() => fetchStudents(retryAttempt + 1), delay);
      } else if (navigator.onLine) {
        console.error('[StudentsContext] network error:', err);
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      const decision = authSessionLoad(event, !!session?.user);
      if (decision === 'load') fetchStudents();
      if (decision === 'clear') { setStudents([]); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onOnline = () => fetchStudents();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StudentsContext.Provider value={{
      students,
      loading,
      getStudentById:  (id)  => students.find(s => s.id === id),
      getStudentByVid: (vid) => students.find(s => s.vid === vid),
      refresh: fetchStudents,
    }}>
      {children}
    </StudentsContext.Provider>
  );
}

export function useStudents() {
  const ctx = useContext(StudentsContext);
  if (!ctx) throw new Error('useStudents must be used within StudentsProvider');
  return ctx;
}
